import { Fragment, useEffect, useState, useCallback, useRef, useMemo } from 'react'
import {
  ArrowUpRight,
  Briefcase,
  ChevronDown,
  FileText,
  Filter,
  Layers,
  Plus,
  Search,
  Target,
  Trash2,
  UserCircle,
  Users,
  X,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { marketSegmentationApi, personaWorkshopApi } from '@/lib/api/client'
import { useAIStore } from '@/stores/aiStore'
import { useSkerpaContextStore } from '@/stores/skerpaContextStore'
import { useEntityCrudListener } from '@/hooks/useEntityListener'
import { PersonaCardDetail } from './PersonaCardDetail'
import { PersonaWorkshopSession } from '@/components/persona-workshop/PersonaWorkshopSession'
import { ImportPersonaDialog } from './ImportPersonaDialog'
import type {
  JtbdData,
  MarketPersona,
  MarketSegmentWithScore,
  PersonaData,
  PersonaType,
  PersonaWorkshopSession as PersonaWorkshopSessionType,
  SegmentPersonaLink,
} from '@ispot/shared'
import { migratePersonaData } from '@ispot/shared'

interface Props {
  accountId: string
  projectId: string
}

function timeAgo(dateStr: string): string {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return days === 1 ? 'yesterday' : `${days}d ago`
}

function completenessScore(data: PersonaData): number {
  let filled = 0
  let total = 4
  if (data.name) filled++
  if (data.role) filled++
  if (data.jtbds?.length > 0) filled++
  const hasPainsOrGains = data.jtbds?.some(j => j.pains.length > 0 || j.gains.length > 0)
  if (hasPainsOrGains) filled++
  return Math.round((filled / total) * 100)
}

function totalPains(jtbds: JtbdData[]): number {
  return jtbds.reduce((sum, j) => sum + j.pains.length, 0)
}

function totalGains(jtbds: JtbdData[]): number {
  return jtbds.reduce((sum, j) => sum + j.gains.length, 0)
}

type StakeholderTypeFilter = 'all' | PersonaType

interface SegmentInfo {
  id: string
  name: string
  coverage?: SegmentPersonaLink['coverage']
}

function computeInitials(data: PersonaData): string {
  if (data.name) {
    return data.name
      .split(' ')
      .map(w => w[0])
      .join('')
      .substring(0, 2)
      .toUpperCase()
  }
  if (data.role) return data.role[0].toUpperCase()
  return '?'
}

function primaryJtbd(data: PersonaData): JtbdData | undefined {
  return data.jtbds?.[0]
}

function findMarketPersona(
  session: PersonaWorkshopSessionType,
  marketPersonas: MarketPersona[],
): MarketPersona | undefined {
  const data = migratePersonaData(session.personaData)
  const normalizedName = data.name.trim().toLowerCase()
  return marketPersonas.find(p => p.linkedWorkshopPersonaId === session.id)
    || (normalizedName ? marketPersonas.find(p => p.name.trim().toLowerCase() === normalizedName) : undefined)
}

function getSessionSegments(
  session: PersonaWorkshopSessionType,
  marketPersonas: MarketPersona[],
  segments: MarketSegmentWithScore[],
  coverage: SegmentPersonaLink[],
): SegmentInfo[] {
  const marketPersona = findMarketPersona(session, marketPersonas)
  const linkedSegments = marketPersona
    ? coverage
        .filter(link => link.personaId === marketPersona.id)
        .map(link => {
          const segment = segments.find(s => s.id === link.segmentId)
          return segment ? { id: segment.id, name: segment.name, coverage: link.coverage } : null
        })
        .filter((segment): segment is SegmentInfo => segment !== null)
    : []

  if (linkedSegments.length > 0) return linkedSegments

  const data = migratePersonaData(session.personaData)
  if (data.segment?.trim()) {
    return [{ id: `legacy:${data.segment.trim().toLowerCase()}`, name: data.segment.trim() }]
  }
  return []
}

function typeFilterLabel(filter: StakeholderTypeFilter): string {
  if (filter === 'all') return 'All'
  return filter === 'buyer' ? 'Buyer' : 'User'
}

function typeBadge(session: PersonaWorkshopSessionType): string {
  return session.personaType === 'buyer' ? 'Buyer' : 'User'
}

function severityTone(severity?: string): string {
  if (severity === 'high') return 'bg-error'
  if (severity === 'medium') return 'bg-solution'
  return 'bg-warm-300'
}

function severityWidth(severity?: string): string {
  if (severity === 'high') return '88%'
  if (severity === 'medium') return '62%'
  return '38%'
}

function importanceTone(importance?: string): string {
  if (importance === 'must_have') return 'bg-market'
  if (importance === 'expected') return 'bg-business'
  return 'bg-warm-300'
}

function importanceWidth(importance?: string): string {
  if (importance === 'must_have') return '88%'
  if (importance === 'expected') return '62%'
  return '38%'
}

export function PersonaConfiguratorPage({ accountId, projectId }: Props) {
  const [sessions, setSessions] = useState<PersonaWorkshopSessionType[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null)
  const [expandedSessionId, setExpandedSessionId] = useState<string | null>(null)
  const [workshopSessionId, setWorkshopSessionId] = useState<string | null>(null)
  const [workshopJtbdTarget, setWorkshopJtbdTarget] = useState<number | 'new' | undefined>(undefined)
  const [showImport, setShowImport] = useState(false)
  const [typeFilter, setTypeFilter] = useState<StakeholderTypeFilter>('all')
  const [segmentFilter, setSegmentFilter] = useState('all')
  const [marketPersonas, setMarketPersonas] = useState<MarketPersona[]>([])
  const [marketSegments, setMarketSegments] = useState<MarketSegmentWithScore[]>([])
  const [segmentCoverage, setSegmentCoverage] = useState<SegmentPersonaLink[]>([])
  const { apiKey } = useAIStore()
  const setActiveResource = useSkerpaContextStore(s => s.setActiveResource)

  // Tell Skerpa which persona is being edited so actions route correctly
  useEffect(() => {
    setActiveResource(activeSessionId)
    return () => setActiveResource(null)
  }, [activeSessionId, setActiveResource])

  const loadSessions = useCallback(async () => {
    setIsLoading(true)
    try {
      const data = await personaWorkshopApi.listSessions(accountId, projectId)
      setSessions(Array.isArray(data) ? data : [])
    } catch {
      setSessions([])
    } finally {
      setIsLoading(false)
    }
  }, [accountId, projectId])

  const loadStakeholderSegments = useCallback(async () => {
    try {
      const bundle = await marketSegmentationApi.getCanvas(accountId, projectId)
      setMarketPersonas(Array.isArray(bundle?.personas) ? bundle.personas : [])
      setMarketSegments(Array.isArray(bundle?.segments) ? bundle.segments : [])
      setSegmentCoverage(Array.isArray(bundle?.coverage) ? bundle.coverage : [])
    } catch {
      setMarketPersonas([])
      setMarketSegments([])
      setSegmentCoverage([])
    }
  }, [accountId, projectId])

  useEffect(() => { loadSessions() }, [loadSessions])
  useEffect(() => { loadStakeholderSegments() }, [loadStakeholderSegments])

  // Refresh when any source creates/updates/deletes a Persona or JTBD entity
  const refreshPersonas = useCallback(() => { loadSessions() }, [loadSessions])
  useEntityCrudListener('Persona', refreshPersonas)
  useEntityCrudListener('CustomerJob', refreshPersonas)
  useEntityCrudListener('CustomerPain', refreshPersonas)
  useEntityCrudListener('CustomerGain', refreshPersonas)

  // Clean up debounce timers on unmount
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current)
    }
  }, [])

  const handleCreate = async () => {
    try {
      const session = await personaWorkshopApi.createSession(accountId, projectId, { personaType: 'user', mode: 'deep' }) as PersonaWorkshopSessionType
      await loadSessions()
      setActiveSessionId(session.id)
    } catch { /* ignore */ }
  }

  const handleDelete = async (id: string) => {
    try {
      await personaWorkshopApi.deleteSession(accountId, projectId, id)
      if (expandedSessionId === id) setExpandedSessionId(null)
      await loadSessions()
    } catch { /* ignore */ }
  }

  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle')
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Ref to hold the latest full merged personaData for the debounced save
  const pendingDataRef = useRef<PersonaData | null>(null)

  const handleUpdate = useCallback((sessionId: string, data: Partial<PersonaData>) => {
    // Optimistic local update — merge immediately, no refetch
    setSessions(prev => prev.map(s => {
      if (s.id !== sessionId) return s
      const current = migratePersonaData(s.personaData)
      const merged = { ...current, ...data } as PersonaData
      pendingDataRef.current = merged
      return { ...s, personaData: merged, updatedAt: new Date().toISOString() }
    }))

    // Debounced API persist — send FULL merged data, not just the partial
    setSaveStatus('saving')
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current)
    saveTimerRef.current = setTimeout(async () => {
      try {
        const toSave = pendingDataRef.current || data
        await personaWorkshopApi.updateSession(accountId, projectId, sessionId, { personaData: toSave })
        setSaveStatus('saved')
        savedTimerRef.current = setTimeout(() => setSaveStatus('idle'), 2000)
      } catch {
        setSaveStatus('idle')
      }
    }, 500)
  }, [accountId, projectId])

  const activeSession = sessions.find(s => s.id === activeSessionId)
  const segmentOptions = useMemo(() => {
    const options = new Map<string, string>()
    marketSegments.forEach(segment => options.set(segment.id, segment.name))
    sessions.forEach(session => {
      getSessionSegments(session, marketPersonas, marketSegments, segmentCoverage).forEach(segment => {
        options.set(segment.id, segment.name)
      })
    })
    return Array.from(options.entries()).map(([id, name]) => ({ id, name }))
  }, [marketSegments, marketPersonas, segmentCoverage, sessions])

  const filteredSessions = useMemo(() => {
    return sessions.filter(session => {
      if (typeFilter !== 'all' && session.personaType !== typeFilter) return false
      if (segmentFilter === 'all') return true
      const applicableSegments = getSessionSegments(session, marketPersonas, marketSegments, segmentCoverage)
      return applicableSegments.some(segment => segment.id === segmentFilter)
    })
  }, [marketPersonas, marketSegments, segmentCoverage, segmentFilter, sessions, typeFilter])

  useEffect(() => {
    if (expandedSessionId && !filteredSessions.some(s => s.id === expandedSessionId)) {
      setExpandedSessionId(null)
    }
  }, [expandedSessionId, filteredSessions])

  const synthesizeProblem = async (sessionId: string, jtbdId: string) => {
    try {
      await personaWorkshopApi.synthesizeProblem(accountId, projectId, sessionId, { jtbdId, apiKey })
      await loadSessions()
    } catch { /* error handled by UI */ }
  }

  const workshopSession = sessions.find(s => s.id === workshopSessionId)
  if (workshopSession) {
    return (
      <PersonaWorkshopSession
        accountId={accountId}
        projectId={projectId}
        session={workshopSession}
        targetJtbd={workshopJtbdTarget}
        onBack={() => { setWorkshopSessionId(null); setWorkshopJtbdTarget(undefined); loadSessions() }}
      />
    )
  }

  if (activeSession) {
    return (
      <PersonaCardDetail
        session={activeSession}
        accountId={accountId}
        projectId={projectId}
        onBack={() => setActiveSessionId(null)}
        onUpdate={(data) => handleUpdate(activeSession.id, data)}
        onSynthesizeProblem={(jtbdId) => synthesizeProblem(activeSession.id, jtbdId)}
        onOpenWorkshop={(id, jtbdTarget) => { setWorkshopJtbdTarget(jtbdTarget); setWorkshopSessionId(id) }}
      />
    )
  }

  return (
    <div className="h-full overflow-auto">
      <div className="max-w-[1000px] mx-auto px-8 py-7">
        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="font-display text-[23px] font-bold tracking-tight">Personas</h1>
            <p className="text-sm text-text-muted mt-0.5">
              Build evidence-backed personas structured around jobs-to-be-done
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowImport(true)}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md border border-border text-text-secondary hover:border-market/40 hover:text-market transition-colors"
            >
              <FileText className="w-3.5 h-3.5" /> Import
            </button>
            <button
              onClick={handleCreate}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md bg-market text-white hover:bg-market-dark transition-colors"
            >
              <Plus className="w-3.5 h-3.5" /> New Persona
            </button>
          </div>
        </div>

        {isLoading && (
          <div className="text-center text-text-muted text-sm py-12">Loading personas...</div>
        )}

        {/* Empty state */}
        {!isLoading && sessions.length === 0 && (
          <div className="py-16 text-center">
            <div className="w-14 h-14 mx-auto mb-5 rounded-2xl bg-warm-100 flex items-center justify-center">
              <Users className="w-6 h-6 text-text-muted" />
            </div>
            <h3 className="font-display text-lg font-semibold mb-1.5">No personas yet</h3>
            <p className="text-sm text-text-muted mb-6 max-w-sm mx-auto leading-relaxed">
              Define who your product serves. Start with a role, then layer in jobs-to-be-done, pains, and gains.
            </p>
            <button
              onClick={handleCreate}
              className="px-5 py-2.5 text-sm font-medium rounded-md bg-market text-white hover:bg-market-dark transition-colors"
            >
              <Plus className="w-3.5 h-3.5 inline mr-1.5" /> Create first persona
            </button>
          </div>
        )}

        {/* Stakeholder map */}
        {!isLoading && sessions.length > 0 && (
          <StakeholderMap
            sessions={filteredSessions}
            totalCount={sessions.length}
            typeFilter={typeFilter}
            segmentFilter={segmentFilter}
            segmentOptions={segmentOptions}
            marketPersonas={marketPersonas}
            marketSegments={marketSegments}
            segmentCoverage={segmentCoverage}
            expandedSessionId={expandedSessionId}
            onTypeFilterChange={setTypeFilter}
            onSegmentFilterChange={setSegmentFilter}
            onToggleExpand={(id) => setExpandedSessionId(current => current === id ? null : id)}
            onOpenFullScreen={setActiveSessionId}
            onDelete={handleDelete}
          />
        )}
      </div>

      <ImportPersonaDialog
        accountId={accountId}
        projectId={projectId}
        open={showImport}
        onClose={() => setShowImport(false)}
        onCreated={(session) => {
          loadSessions()
          setActiveSessionId(session.id)
        }}
      />
    </div>
  )
}

interface StakeholderMapProps {
  sessions: PersonaWorkshopSessionType[]
  totalCount: number
  typeFilter: StakeholderTypeFilter
  segmentFilter: string
  segmentOptions: Array<{ id: string; name: string }>
  marketPersonas: MarketPersona[]
  marketSegments: MarketSegmentWithScore[]
  segmentCoverage: SegmentPersonaLink[]
  expandedSessionId: string | null
  onTypeFilterChange: (filter: StakeholderTypeFilter) => void
  onSegmentFilterChange: (segmentId: string) => void
  onToggleExpand: (sessionId: string) => void
  onOpenFullScreen: (sessionId: string) => void
  onDelete: (sessionId: string) => void
}

function StakeholderMap({
  sessions,
  totalCount,
  typeFilter,
  segmentFilter,
  segmentOptions,
  marketPersonas,
  marketSegments,
  segmentCoverage,
  expandedSessionId,
  onTypeFilterChange,
  onSegmentFilterChange,
  onToggleExpand,
  onOpenFullScreen,
  onDelete,
}: StakeholderMapProps) {
  return (
    <section>
      <div className="flex flex-col gap-2 border-y border-border-subtle py-3 sm:flex-row sm:items-center sm:justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-text-muted">Stakeholder map</span>
          <span className="text-[11px] text-text-muted tabular-nums">{sessions.length} of {totalCount}</span>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex gap-0.5">
            {(['all', 'user', 'buyer'] as StakeholderTypeFilter[]).map(filter => (
              <button
                key={filter}
                onClick={() => onTypeFilterChange(filter)}
                className={cn(
                  'rounded-md px-3 py-1 text-[12px] font-medium transition-colors',
                  typeFilter === filter
                    ? 'bg-warm-100 text-text'
                    : 'text-text-muted hover:text-text-secondary',
                )}
              >
                {typeFilterLabel(filter)}
              </button>
            ))}
          </div>

          <label className="relative inline-flex items-center">
            <Filter className="pointer-events-none absolute left-2.5 h-3.5 w-3.5 text-text-muted" />
            <select
              value={segmentFilter}
              onChange={(e) => onSegmentFilterChange(e.target.value)}
              className="h-7 appearance-none rounded-md bg-transparent pl-7 pr-7 text-[12px] font-medium text-text-secondary outline-none transition-colors hover:bg-warm-100 focus:bg-warm-100"
            >
              <option value="all">All segments</option>
              {segmentOptions.map(segment => (
                <option key={segment.id} value={segment.id}>{segment.name}</option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-2 h-3.5 w-3.5 text-text-muted" />
          </label>
        </div>
      </div>

      {sessions.length === 0 ? (
        <div className="px-8 py-14 text-center">
          <Search className="mx-auto mb-3 h-5 w-5 text-text-muted" />
          <h3 className="font-display text-[16px] font-semibold">No stakeholders match this filter</h3>
          <p className="mt-1 text-sm text-text-muted">Try a different type or applicable segment.</p>
        </div>
      ) : (
        <div
          className="grid grid-cols-1 md:grid-cols-2"
          style={{
            borderTop: '1px solid var(--color-border)',
            borderLeft: '1px solid var(--color-border)',
          }}
        >
          {sessions.map(session => {
            const data = migratePersonaData(session.personaData)
            const segments = getSessionSegments(session, marketPersonas, marketSegments, segmentCoverage)
            const expanded = expandedSessionId === session.id
            return (
              <Fragment key={session.id}>
                <StakeholderPersonaCard
                  session={session}
                  data={data}
                  segments={segments}
                  expanded={expanded}
                  onToggle={() => onToggleExpand(session.id)}
                  onOpenFullScreen={() => onOpenFullScreen(session.id)}
                  onDelete={() => onDelete(session.id)}
                />
                {expanded && (
                  <InlinePersonaReveal
                    session={session}
                    data={data}
                    segments={segments}
                    onCollapse={() => onToggleExpand(session.id)}
                    onOpenFullScreen={() => onOpenFullScreen(session.id)}
                  />
                )}
              </Fragment>
            )
          })}
        </div>
      )}
    </section>
  )
}

interface StakeholderPersonaCardProps {
  session: PersonaWorkshopSessionType
  data: PersonaData
  segments: SegmentInfo[]
  expanded: boolean
  onToggle: () => void
  onOpenFullScreen: () => void
  onDelete: () => void
}

function StakeholderPersonaCard({
  session,
  data,
  segments,
  expanded,
  onToggle,
  onOpenFullScreen,
  onDelete,
}: StakeholderPersonaCardProps) {
  const isBuyer = session.personaType === 'buyer'
  const jtbd = primaryJtbd(data)
  const score = completenessScore(data)
  const pains = totalPains(data.jtbds || [])
  const gains = totalGains(data.jtbds || [])
  const initials = computeInitials(data)

  const statusLabel = score >= 100 ? 'Complete' : score >= 45 ? 'In progress' : 'Started'
  const quoteText = data.quote || jtbd?.title

  const domainColor = isBuyer ? 'var(--color-business)' : 'var(--color-market)'
  const domainText = isBuyer ? 'var(--color-business-700)' : 'var(--color-market-700)'

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onToggle}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onToggle()
        }
      }}
      className={cn(
        'group relative cursor-pointer px-4 py-4 text-left transition-colors duration-150',
        expanded ? 'bg-warm-100' : 'hover:bg-warm-50',
      )}
      style={{
        borderRight: '1px solid var(--color-border)',
        borderBottom: '1px solid var(--color-border)',
      }}
    >
      {/* Header: avatar + identity + actions */}
      <div className="flex items-center gap-3 mb-2.5">
        <div
          className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full text-sm font-semibold text-white"
          style={{ backgroundColor: data.photo ? 'var(--color-warm-300)' : domainColor }}
        >
          {data.photo ? <img src={data.photo} alt="" className="h-full w-full object-cover" /> : initials}
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="truncate font-display text-[15px] font-semibold leading-tight tracking-tight">
            {data.name || data.role || 'New persona'}
          </h3>
          <p className="mt-0.5 truncate text-[12px] text-text-secondary">
            {data.role || 'Role not defined'}
          </p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <span
            className="text-[10px] font-semibold uppercase tracking-[0.1em]"
            style={{ color: domainText }}
          >
            {typeBadge(session)}
          </span>
          <div className="flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onOpenFullScreen() }}
              className="rounded p-1 text-text-muted transition-colors hover:bg-warm-200 hover:text-text"
              title="Open full screen"
            >
              <ArrowUpRight className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onDelete() }}
              className="rounded p-1 text-text-muted transition-colors hover:bg-error/10 hover:text-error"
              title="Delete persona"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* Quote */}
      {quoteText ? (
        <p className="mb-3 line-clamp-2 max-w-[54ch] text-[12.5px] italic leading-relaxed text-text-secondary">
          <span aria-hidden="true">&ldquo;</span>{quoteText}<span aria-hidden="true">&rdquo;</span>
        </p>
      ) : (
        <p className="mb-3 text-[12px] text-text-muted italic">No quote yet</p>
      )}

      {/* Meta strip */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[11.5px] text-text-muted">
        {segments.length > 0 ? (
          <>
            {segments.slice(0, 2).map(segment => (
              <span
                key={segment.id}
                className="inline-flex items-center rounded-full bg-warm-100 px-2 py-0.5 text-[11px] font-medium text-text-secondary"
              >
                {segment.name}
              </span>
            ))}
            {segments.length > 2 && (
              <span className="inline-flex items-center rounded-full bg-warm-100 px-2 py-0.5 text-[11px] font-medium text-text-muted">
                +{segments.length - 2}
              </span>
            )}
          </>
        ) : (
          <span className="text-[11px] italic text-text-muted">No segment</span>
        )}

        <span className="tabular-nums text-text-secondary">
          <span className="font-medium text-text">{(data.jtbds || []).length}</span> JTBD ·{' '}
          <span className="font-medium text-text">{pains}</span> pains ·{' '}
          <span className="font-medium text-text">{gains}</span> outcomes
        </span>

        <span className="ml-auto inline-flex items-center gap-1.5">
          <span
            className="h-1.5 w-1.5 rounded-full"
            style={{
              backgroundColor: score >= 100
                ? 'var(--color-market)'
                : score >= 45
                  ? 'var(--color-business)'
                  : 'var(--color-warm-400)',
            }}
          />
          <span>{statusLabel} · {timeAgo(session.updatedAt)}</span>
        </span>
      </div>
    </div>
  )
}

interface InlinePersonaRevealProps {
  session: PersonaWorkshopSessionType
  data: PersonaData
  segments: SegmentInfo[]
  onCollapse: () => void
  onOpenFullScreen: () => void
}

function InlinePersonaReveal({
  session,
  data,
  segments,
  onCollapse,
  onOpenFullScreen,
}: InlinePersonaRevealProps) {
  const isBuyer = session.personaType === 'buyer'
  const jtbd = primaryJtbd(data)
  const pains = jtbd?.pains || []
  const gains = jtbd?.gains || []
  const initials = computeInitials(data)
  const jtbdCount = (data.jtbds || []).length
  const totalPainCount = totalPains(data.jtbds || [])
  const totalGainCount = totalGains(data.jtbds || [])

  const severityDot = (severity?: string) =>
    severity === 'high' ? 'bg-error' : severity === 'medium' ? 'bg-solution' : 'bg-warm-400'
  const severityLabel = (severity?: string) =>
    severity === 'high' ? 'Critical' : severity === 'medium' ? 'Moderate' : 'Minor'
  const importanceDot = (importance?: string) =>
    importance === 'must_have' ? 'bg-market' : importance === 'expected' ? 'bg-solution' : 'bg-warm-400'
  const importanceLabel = (importance?: string) =>
    importance === 'must_have' ? 'Critical' : importance === 'expected' ? 'Important' : 'Nice to have'

  const revealDomainColor = isBuyer ? 'var(--color-business)' : 'var(--color-market)'
  const revealDomainText = isBuyer ? 'var(--color-business-700)' : 'var(--color-market-700)'

  return (
    <article
      className="md:col-span-2 animate-in fade-in-0 duration-200"
      style={{ borderRight: '1px solid var(--color-border)', borderBottom: '1px solid var(--color-border)' }}
    >
      <div className="bg-warm-50 px-6 py-5">
        {/* Header: avatar + name/role + actions */}
        <div className="mb-4 grid grid-cols-[56px_1fr_max-content] items-start gap-4">
          <div
            className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-full text-base font-semibold text-white"
            style={{ backgroundColor: data.photo ? 'var(--color-warm-300)' : revealDomainColor }}
          >
            {data.photo ? <img src={data.photo} alt="" className="h-full w-full object-cover" /> : initials}
          </div>
          <div className="min-w-0 pt-0.5">
            <div className="flex items-baseline gap-2 flex-wrap">
              <h3 className="font-display text-[20px] font-semibold leading-tight tracking-tight">
                {data.name || 'New persona'}
              </h3>
              {data.archetype && (
                <span
                  className="font-display text-[13px] italic"
                  style={{ color: revealDomainText }}
                >
                  {data.archetype}
                </span>
              )}
            </div>
            <p className="mt-0.5 text-[13px] text-text-secondary">
              {data.role || 'Role not defined'}
            </p>
          </div>
          <div className="flex items-center gap-1 pt-0.5">
            <button
              type="button"
              onClick={onOpenFullScreen}
              className="inline-flex items-center gap-1 rounded px-2 py-1.5 text-[12px] font-medium text-text-secondary transition-colors hover:bg-white/50 hover:text-text"
            >
              Edit <ArrowUpRight className="h-3 w-3" />
            </button>
            <button
              type="button"
              onClick={onCollapse}
              className="rounded p-1.5 text-text-muted transition-colors hover:bg-white/50 hover:text-text"
              title="Collapse"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        {/* Quote / context */}
        {(data.quote || data.context) && (
          <p
            className="mb-5 max-w-[60ch] font-display text-[15px] italic leading-relaxed"
            style={{ color: 'var(--color-text)' }}
          >
            <span className="mr-1 text-[20px] leading-none opacity-30" aria-hidden="true">&ldquo;</span>
            {data.quote || data.context}
          </p>
        )}

        {/* Primary JTBD */}
        {jtbd ? (
          <>
            <div className="mb-4">
              <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-text-muted">
                Primary job to be done{jtbdCount > 1 ? ` · 1 of ${jtbdCount}` : ''}
              </div>
              <h3 className="mb-1 font-display text-[17px] font-semibold leading-tight tracking-tight">
                {jtbd.title || 'Untitled job'}
              </h3>
              {jtbd.functional && (
                <p className="max-w-[64ch] text-[13.5px] leading-relaxed text-text-secondary">
                  {jtbd.functional}
                </p>
              )}
            </div>

            {/* Pains and gains 2-col */}
            {(pains.length > 0 || gains.length > 0) && (
              <div className="grid grid-cols-1 gap-x-10 gap-y-4 border-t border-border-subtle pt-4 md:grid-cols-2">
                <section>
                  <div className="mb-2 flex items-baseline justify-between text-[11px] font-semibold uppercase tracking-[0.12em] text-text">
                    <span>Key pains</span>
                    <span className="font-normal normal-case tracking-normal text-text-muted tabular-nums">{pains.length}</span>
                  </div>
                  <ul className="m-0 list-none space-y-1.5 p-0">
                    {pains.slice(0, 4).map(p => (
                      <li key={p.id} className="grid grid-cols-[10px_1fr_max-content] items-baseline gap-3">
                        <span className={cn('mt-1 h-1.5 w-1.5 rounded-full', severityDot(p.severity))} />
                        <span className="truncate text-[13px] text-text">{p.description}</span>
                        <span className="text-[10.5px] uppercase tracking-[0.06em] text-text-muted">{severityLabel(p.severity)}</span>
                      </li>
                    ))}
                  </ul>
                  {pains.length > 4 && (
                    <div className="mt-2 text-[11px] text-text-muted">+{pains.length - 4} more</div>
                  )}
                </section>

                <section>
                  <div className="mb-2 flex items-baseline justify-between text-[11px] font-semibold uppercase tracking-[0.12em] text-text">
                    <span>Desired outcomes</span>
                    <span className="font-normal normal-case tracking-normal text-text-muted tabular-nums">{gains.length}</span>
                  </div>
                  <ul className="m-0 list-none space-y-1.5 p-0">
                    {gains.slice(0, 4).map(g => (
                      <li key={g.id} className="grid grid-cols-[10px_1fr_max-content] items-baseline gap-3">
                        <span className={cn('mt-1 h-1.5 w-1.5 rounded-full', importanceDot(g.importance))} />
                        <span className="truncate text-[13px] text-text">{g.description}</span>
                        <span className="text-[10.5px] uppercase tracking-[0.06em] text-text-muted">{importanceLabel(g.importance)}</span>
                      </li>
                    ))}
                  </ul>
                  {gains.length > 4 && (
                    <div className="mt-2 text-[11px] text-text-muted">+{gains.length - 4} more</div>
                  )}
                </section>
              </div>
            )}

            {/* User story */}
            <div className="mt-5 border-t border-border-subtle pt-4">
              <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-text-muted">User story</div>
              <p className="max-w-[64ch] font-display text-[14px] italic leading-relaxed text-text-secondary">
                {jtbd.customerProblem?.statement
                  || `As ${data.name || data.role || 'this stakeholder'}, I want ${jtbd.title.toLowerCase()} so I can make progress without avoidable friction.`}
              </p>
            </div>
          </>
        ) : (
          <p className="px-4 py-5 text-center text-sm italic text-text-muted">
            No primary JTBD yet. Open full screen to add one.
          </p>
        )}

        {/* Footer meta */}
        <div className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-border-subtle pt-3 text-[11px] text-text-muted">
          <span className="tabular-nums">
            <span className="font-medium text-text">{jtbdCount}</span> JTBD ·{' '}
            <span className="font-medium text-text">{totalPainCount}</span> pains ·{' '}
            <span className="font-medium text-text">{totalGainCount}</span> outcomes
          </span>
          {segments.length > 0 && (
            <>
              <span className="h-1 w-1 rounded-full bg-warm-300" />
              <span>
                {segments.slice(0, 2).map(s => s.name).join(', ')}
                {segments.length > 2 && `, +${segments.length - 2}`}
              </span>
            </>
          )}
          <span className="ml-auto">Updated {timeAgo(session.updatedAt)}</span>
        </div>
      </div>
    </article>
  )
}
